<template>
  <div>
    <Head title="Create Staff" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/staffs/nafa-staffs">
        {{ $t('message.staffs.staffs', 2) }}
      </Link>
      <span class="text-indigo-400 font-medium">/</span> {{ $t('message.general.create') }}
    </h1>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <!-- Stepper -->
      <div class="px-4 py-5">
        <div class="flex items-center justify-between mb-8">
          <div v-for="(step, index) in steps" :key="index" class="flex items-center">
            <div :class="['w-8 h-8 rounded-full flex items-center justify-center', currentStep > index ? 'bg-indigo-600 text-white' : currentStep === index ? 'bg-indigo-200 text-indigo-600' : 'bg-gray-200 text-gray-600']">
              {{ index + 1 }}
            </div>
            <div v-if="index < steps.length - 1" class="mx-2 w-24 h-1" :class="currentStep > index ? 'bg-indigo-600' : 'bg-gray-200'"></div>
            <div class="text-sm" :class="currentStep >= index ? 'text-indigo-600' : 'text-gray-600'">{{ step }}</div>
          </div>
        </div>
      </div>

      <form @submit.prevent="store">
        <div class="p-8">
          <!-- Step 1: Basic Information -->
          <div v-show="currentStep === 0" class="w-full">
            <div class="flex flex-wrap -mx-3">
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.first_name" :error="form.errors.first_name" :label="$t('message.staffs.first_name')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.last_name" :error="form.errors.last_name" :label="$t('message.staffs.last_name')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.email" :error="form.errors.email" type="email" pattern=".+@nafafs\.com" :title="$t('message.login.email_title')" :label="$t('message.staffs.email')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.password" :error="form.errors.password" type="password" autocomplete="new-password" :label="$t('message.staffs.password')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <select-input v-model="form.account_id" :error="form.errors.account_id" :label="$t('message.staffs.country')">
                  <option :value="null" />
                  <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.country }} - {{ country.flag }}</option>
                </select-input>
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <select-input v-model="form.department_id" :error="form.errors.department_id" :label="$t('message.staffs.department')">
                  <option :value="null" />
                  <option v-for="department in departments" :key="department.id" :value="department.id">{{ $t(`message.departments.${slugifyDepartment(department.name)}`) }} - {{ department.account.country }} {{ department.account.flag }}</option>
                </select-input>
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <select-input v-model="form.user_type_id" :error="form.errors.user_type_id" :label="$t('message.staffs.role')">
                  <option :value="null" />
                  <option v-for="user_type in user_types" :key="user_type.id" :value="user_type.id">
                    {{ $t(`message.staffs.${slugifyRole(user_type.name)}`) }}
                  </option>
                </select-input>
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.phone" :error="form.errors.phone" :label="$t('message.staffs.phone')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.nafa_id" :error="form.errors.nafa_id" :label="$t('message.staffs.nafa_id')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.designation" :error="form.errors.designation" :label="$t('message.staffs.designation')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <date-picker v-model="form.date_joined" :error="form.errors.date_joined" :label="$t('message.staffs.date_employed')" />
              </div>
            </div>
          </div>

          <!-- Step 2: Personal Information -->
          <div v-show="currentStep === 1" class="w-full">
            <div class="flex flex-wrap -mx-3">
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.id_number" :error="form.errors.id_number" :label="$t('message.staffs.id_number')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.social_security_number" :error="form.errors.social_security_number" :label="$t('message.staffs.social_security_number')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.tin_number" :error="form.errors.tin_number" :label="$t('message.staffs.tin_number')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <date-picker v-model="form.date_of_birth" :error="form.errors.date_of_birth" :label="$t('message.staffs.date_of_birth')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.nationality" :error="form.errors.nationality" :label="$t('message.staffs.nationality')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <select-input v-model="form.gender" :error="form.errors.gender" :label="$t('message.general.gender')">
                  <option :value="null" />
                  <option v-for="gender in genders" :key="gender.id" :value="gender.id">
                    {{ $t(`message.general.${slugifyGender(gender.name)}`) }}
                  </option>
                </select-input>
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <select-input v-model="form.marital_status" :error="form.errors.marital_status" :label="$t('message.staffs.marital_status')">
                  <option :value="null" />
                  <option v-for="status in maritalStatuses" :key="status.id" :value="status.id">
                    {{ $t(`message.staffs.${status.name.toLowerCase()}`) }}
                  </option>
                </select-input>
              </div>
              <div class="mb-6 px-3 w-full">
                <text-input v-model="form.residential_address" :error="form.errors.residential_address" :label="$t('message.staffs.residential_address')" />
              </div>
            </div>
          </div>

          <!-- Step 3: Emergency Contact Information -->
          <div v-show="currentStep === 2" class="w-full">
            <div class="flex flex-wrap -mx-3">
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.emergency_contact_name" :error="form.errors.emergency_contact_name" :label="$t('message.staffs.emergency_contact_name')" />
              </div>
              <div class="mb-6 px-3 w-full lg:w-1/2">
                <text-input v-model="form.emergency_contact_number" :error="form.errors.emergency_contact_number" :label="$t('message.staffs.emergency_contact_number')" />
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button type="button" class="btn-gray" :disabled="currentStep === 0" @click="previousStep">Previous</button>
          <div>
            <button v-if="currentStep < steps.length - 1" type="button" class="btn-indigo" @click="nextStep">Next</button>
            <loading-button v-else :loading="form.processing" class="btn-indigo" type="submit">
              {{ $t('message.staffs.create_staff') }}
            </loading-button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import DatePicker from '@/Shared/DatePicker'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    DatePicker,
  },
  layout: Layout,
  props: {
    departments: Array,
    user_types: Array,
    countries: Array,
  },
  remember: 'form',
  data() {
    return {
      currentStep: 0,
      steps: ['Basic Information', 'Personal Information', 'Emergency Contact'],
      form: this.$inertia.form({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        phone: '',
        date_joined: '',
        nafa_id: '',
        gender: '',
        designation: '',
        user_type_id: null,
        department_id: null,
        account_id: null,
        id_number: '',
        social_security_number: '',
        tin_number: '',
        date_of_birth: '',
        nationality: '',
        residential_address: '',
        marital_status: null,
        emergency_contact_name: '',
        emergency_contact_number: '',
      }),
      genders: [
        { id: 'male', name: 'Male' },
        { id: 'female', name: 'Female' },
      ],
      maritalStatuses: [
        { id: 'single', name: 'Single' },
        { id: 'married', name: 'Married' },
        { id: 'divorced', name: 'Divorced' },
        { id: 'separated', name: 'Separated' },
        { id: 'widowed', name: 'Widowed' },
      ],
    }
  },
  watch: {
    'form.email': function (value) {
      this.form.email = value.toLowerCase()
    },
  },
  methods: {
    nextStep() {
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++
      }
    },
    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },
    store() {
      this.form.post('/staffs/nafa-staffs')
    },
    slugifyRole(role) {
      return role.toLowerCase().replace(' ', '_').replace('-', '_')
    },
    slugifyDepartment(department) {
      if (department.charAt(department.length - 1) == '.') {
        department = department.slice(0, -1)
      }
      return department.toLowerCase().replace(/\./g, '').replace(/&/g, 'and').replace(/\s+/g, '_')
    },
    slugifyGender(gender) {
      return gender.toLowerCase()
    },
  },
}
</script>

<style scoped>
.btn-gray {
  @apply px-4 py-2 text-gray-700 text-sm font-medium hover:bg-gray-50 bg-white border border-gray-300 rounded-md focus:outline-none shadow-sm focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2;
}
</style>
